import React, { useCallback, useEffect, useRef } from "react";
import ReactJson from "react18-json-view";
import "react18-json-view/src/style.css";
import { useTermSelection } from "../../../hooks/useTermSelection";

type JSONDisplayProps = {
  data: any;
  searchQuery?: string;
  highlightDuration?: number;
  isCurrentSearchResult?: boolean;
  currentOccurrenceIndex?: number | undefined;
  onTermSelect?: (term: string) => void;
};

const JSONDisplay: React.FC<JSONDisplayProps> = ({
  data,
  searchQuery,
  highlightDuration = 2000,
  isCurrentSearchResult = false,
  currentOccurrenceIndex,
  onTermSelect,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const highlightedElementsRef = useRef<Element[]>([]);

  const { handleDoubleClickEvent, handleClick } = useTermSelection({
    onTermSelect,
  });

  const scrollToKey = useCallback((key: string) => {
    if (!containerRef.current) return;
    const walker = document.createTreeWalker(
      containerRef.current,
      NodeFilter.SHOW_TEXT,
      null
    );
    let node: Node | null;
    while ((node = walker.nextNode())) {
      if (node.textContent?.trim() === key && node.parentElement) {
        node.parentElement.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
  }, []);

  useEffect(() => {
    highlightedElementsRef.current.forEach((el) => {
      el.classList.remove("search-highlight");
      el.classList.remove("search-highlight-current");
    });
    highlightedElementsRef.current = [];

    if (!searchQuery || !containerRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      const query = searchQuery.toLowerCase();
      const container = containerRef.current;
      if (!container) return;

      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        null
      );

      const matchingElements: Element[] = [];
      let node: Node | null;
      let matchIndex = 0;

      while ((node = walker.nextNode())) {
        const textContent = node.textContent?.toLowerCase() || "";
        if (textContent.includes(query) && node.parentElement) {
          matchingElements.push(node.parentElement);
          // this is for identifying if an object is highlighted yellow (current) or blue (next search result), and give the appropriate css class
          const isThisOccurrence =
            isCurrentSearchResult && currentOccurrenceIndex === matchIndex;
          const highlightClass = isThisOccurrence
            ? "search-highlight-current"
            : "search-highlight";
          node.parentElement.classList.add(highlightClass);
          matchIndex++;
        }
      }

      highlightedElementsRef.current = matchingElements;

      if (
        isCurrentSearchResult &&
        currentOccurrenceIndex !== undefined &&
        matchingElements[currentOccurrenceIndex]
      ) {
        matchingElements[currentOccurrenceIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (matchingElements.length > 0) {
        matchingElements[0]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [
    searchQuery,
    data,
    highlightDuration,
    isCurrentSearchResult,
    currentOccurrenceIndex,
  ]);

  const navKeys = [
    { label: "Text", key: "text" },
    { label: "Molecules", key: "molecules" },
    { label: "Reactions", key: "rxns" },
    { label: "Tables", key: "tables" },
  ];

  return (
    <div className="bg-white shadow-[0_2px_8px_rgba(0,0,0,0.7)] flex flex-col h-full flex-grow">
      <div className="flex gap-1 px-4 pt-3 pb-2 border-b border-gray-200">
        {navKeys.map(({ label, key }) => (
          <button
            key={key}
            onClick={() => scrollToKey(key)}
            className="px-3 py-1 text-xs font-medium rounded text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            {label}
          </button>
        ))}
      </div>
      <div
        ref={containerRef}
        className="flex-grow overflow-y-auto p-4"
        onClick={handleClick}
        onDoubleClick={handleDoubleClickEvent}
      >
        <ReactJson
          src={data}
          theme="github"
          collapsed={false}
          enableClipboard={false}
          style={{
            width: "100%",
            backgroundColor: "transparent",
            fontFamily: "inherit",
          }}
        />
      </div>
    </div>
  );
};

export default JSONDisplay;
